import type { z } from '@seek/indie-api-types';
import type { ComponentProps } from 'react';
import type { TextLink } from 'braid-design-system';
import { buildFormSchema } from './buildFormSchema.ts';
import { formConfig } from './formConfig.ts';

type ValueTypeMap = {
  string: string;
  number: number;
  stringArray: string[];
  numberArray: number[];
};

export type PossibleValues = ValueTypeMap[keyof ValueTypeMap] | undefined;

type SelectOption<T extends number | string> = {
  id: string;
  label: string;
  value: T;
  image?: {
    src: string;
    alt: string;
    height?: number;
    width?: number;
  };
  disabled?: boolean;
};

export type LabelLink = {
  text: string;
  url: string;
  target?: ComponentProps<typeof TextLink>['target'];
  hitArea?: ComponentProps<typeof TextLink>['hitArea'];
  weight?: ComponentProps<typeof TextLink>['weight'];
};

type BaseFormField<T extends keyof ValueTypeMap> = {
  id: string;
  label: string;
  labelLinks?: LabelLink[];
  type: T;
  initialValue: ValueTypeMap[T];
  secondaryLabel?: string;
  required: boolean;
  placeholder?: string;
  disabled?: boolean;
  helpText?: string;
  dependsOn?: {
    logicalOperator: 'or' | 'and';
    dependencies: Array<{
      id: string;
      value: PossibleValues;
    }>;
  };
};

export type FormFieldWithOptions =
  | (BaseFormField<'string'> & {
      inputType: 'Select';
      other: boolean;
      optionsColumn: boolean;
      options: Array<SelectOption<string>>;
    })
  | (BaseFormField<'stringArray'> & {
      inputType: 'MultiSelect';
      other: boolean;
      optionsColumn: boolean;
      options: Array<SelectOption<string>>;
    })
  | (BaseFormField<'number'> & {
      inputType: 'Select';
      other: boolean;
      optionsColumn: boolean;
      options: Array<SelectOption<number>>;
    })
  | (BaseFormField<'numberArray'> & {
      inputType: 'MultiSelect';
      other: boolean;
      optionsColumn: boolean;
      options: Array<SelectOption<number>>;
    });

export type DateFormField = BaseFormField<'string'> &
  (
    | { inputType: 'Date'; future: true; past?: never }
    | { inputType: 'Date'; past: true; future?: never }
    | { inputType: 'Date'; future?: never; past?: never }
  );

export type TextFormFields = BaseFormField<'string'> & {
  inputType: 'TextField' | 'TextArea';
  minLength?: number;
  maxLength?: number;
};

export type SpecialTextFormFields = BaseFormField<'string'> & {
  inputType: 'Email' | 'Phone' | 'Url';
};

export type NumberFormFields = BaseFormField<'number'> & {
  inputType: 'Number';
  min?: number;
  max?: number;
};

type FormFieldWithoutOptions =
  | TextFormFields
  | SpecialTextFormFields
  | NumberFormFields
  | DateFormField;

export type FormField = FormFieldWithOptions | FormFieldWithoutOptions;

export type FormConfig = FormField[];

export type ExtractFormValues<T extends readonly FormField[]> = {
  [K in T[number]['id']]+?: Extract<T[number], { id: K }>['initialValue'];
} & {
  [K in T[number]['id'] as Extract<
    T[number],
    { id: K }
  >['required'] extends true
    ? K
    : never]-?: Extract<T[number], { id: K }>['initialValue'];
};

export type FormValues = ExtractFormValues<typeof formConfig>;

export const FormSchema = buildFormSchema(formConfig);

export type FormSchema = z.infer<typeof FormSchema>;
